import { useEffect } from "react";
import {
  lockPageScroll,
  unlockPageScroll,
} from "../helpers/scrollPageToTop.ts";

const SKIP_INPUT_TYPES = new Set([
  "button",
  "submit",
  "reset",
  "checkbox",
  "radio",
  "file",
  "hidden",
  "image",
  "range",
  "color",
]);

const isKeyboardField = (element: EventTarget | null): element is HTMLElement => {
  if (!(element instanceof HTMLElement)) return false;
  if (element instanceof HTMLTextAreaElement) return true;
  if (element.isContentEditable) return true;
  if (!(element instanceof HTMLInputElement)) return false;
  return !SKIP_INPUT_TYPES.has((element.type || "text").toLowerCase());
};

const MOBILE_SHEET_MQ = "(max-width: 960px)";
const KEYBOARD_OVERLAP_PX = 80;

function isMobileSheet() {
  return window.matchMedia(MOBILE_SHEET_MQ).matches;
}

function findScrollableParent(field: HTMLElement): HTMLElement | null {
  let node = field.parentElement;
  while (node && node !== document.body) {
    const overflowY = window.getComputedStyle(node).overflowY;
    const canScroll = overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
    if (canScroll && node.scrollHeight > node.clientHeight + 1) return node;
    node = node.parentElement;
  }
  return null;
}

/** Keyboard overlap that the layout viewport still draws underneath. */
function keyboardOverlap() {
  const viewport = window.visualViewport;
  if (!viewport) return 0;
  return Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
}

function resetDialogBox(modal: HTMLElement) {
  const dialog = modal.querySelector<HTMLElement>(".modal-dialog");
  if (!dialog) return;
  dialog.style.removeProperty("height");
  dialog.style.removeProperty("max-height");
  dialog.style.removeProperty("top");
  dialog.style.removeProperty("bottom");
}

function clearKeyboardFit(modal: HTMLElement) {
  modal.classList.remove("is-keyboard-fit");
  modal.style.removeProperty("--keyboard-inset");
  resetDialogBox(modal);
  modal.querySelectorAll<HTMLElement>(".bet-slip, .props-bet-slip").forEach((slip) => {
    slip.style.removeProperty("padding-bottom");
    slip.style.removeProperty("box-sizing");
  });
  modal.querySelectorAll<HTMLElement>(".bet-slip__footer").forEach((footer) => {
    footer.classList.remove("is-pinned-above-keyboard");
    footer.style.removeProperty("top");
    footer.style.removeProperty("bottom");
  });
}

/** Do not resize the slip when the keyboard opens. Padding the sheet moves the stake boxes. */
function liftSlipAboveKeyboard(_modal: HTMLElement, _baselineHeight: number) {
  return;
}

function revealFocusedField(field: HTMLElement) {
  if (!isMobileSheet()) return;
  if (field.closest(".bet-slip__footer, .props-bet-slip__footer")) return;
  const scroller = findScrollableParent(field);
  if (!scroller) return;

  const padding = 12;
  const fieldRect = field.getBoundingClientRect();
  const scrollerRect = scroller.getBoundingClientRect();
  const bottomLimit = scrollerRect.bottom - padding;
  const topLimit = scrollerRect.top + padding;

  if (fieldRect.bottom > bottomLimit) {
    scroller.scrollTop += fieldRect.bottom - bottomLimit;
  } else if (fieldRect.top < topLimit) {
    scroller.scrollTop -= topLimit - fieldRect.top;
  }
}

/** Keep the page behind the bet sheet still. On keyboard, keep the focused field above it. */
export function useBetModalPageLock(modalId: string) {
  useEffect(() => {
    const modalElement = document.getElementById(modalId);
    if (!modalElement) return;

    let activeField: HTMLElement | null = null;
    let revealFrame = 0;
    let baselineHeight = window.innerHeight;
    const revealTimers: number[] = [];

    const rememberBaseline = () => {
      if (keyboardOverlap() < KEYBOARD_OVERLAP_PX) {
        baselineHeight = window.innerHeight;
      }
    };

    const clearRevealTimers = () => {
      revealTimers.forEach((id) => window.clearTimeout(id));
      revealTimers.length = 0;
      if (revealFrame) cancelAnimationFrame(revealFrame);
      revealFrame = 0;
    };

    const refit = () => {
      if (!activeField || !activeField.isConnected || !modalElement.contains(activeField)) return;
      liftSlipAboveKeyboard(modalElement, baselineHeight);
      if (revealFrame) cancelAnimationFrame(revealFrame);
      revealFrame = requestAnimationFrame(() => {
        revealFrame = 0;
        if (!activeField || !activeField.isConnected) return;
        revealFocusedField(activeField);
      });
    };

    const scheduleRefit = () => {
      clearRevealTimers();
      revealTimers.push(window.setTimeout(refit, 300));
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (!isKeyboardField(event.target)) return;
      rememberBaseline();
      activeField = event.target;
      scheduleRefit();
    };

    const handleFocusOut = () => {
      window.setTimeout(() => {
        const next = document.activeElement;
        if (isKeyboardField(next) && modalElement.contains(next)) {
          activeField = next;
          return;
        }
        activeField = null;
        clearRevealTimers();
        clearKeyboardFit(modalElement);
      }, 80);
    };

    const onShow = () => {
      clearKeyboardFit(modalElement);
      rememberBaseline();
      lockPageScroll();
    };
    const onHidden = () => {
      activeField = null;
      clearRevealTimers();
      clearKeyboardFit(modalElement);
      unlockPageScroll();
    };

    const viewport = window.visualViewport;
    modalElement.addEventListener("show.bs.modal", onShow);
    modalElement.addEventListener("hidden.bs.modal", onHidden);
    modalElement.addEventListener("focusin", handleFocusIn);
    modalElement.addEventListener("focusout", handleFocusOut);
    viewport?.addEventListener("resize", refit);
    viewport?.addEventListener("scroll", refit);

    return () => {
      activeField = null;
      clearRevealTimers();
      clearKeyboardFit(modalElement);
      modalElement.removeEventListener("show.bs.modal", onShow);
      modalElement.removeEventListener("hidden.bs.modal", onHidden);
      modalElement.removeEventListener("focusin", handleFocusIn);
      modalElement.removeEventListener("focusout", handleFocusOut);
      viewport?.removeEventListener("resize", refit);
      viewport?.removeEventListener("scroll", refit);
      unlockPageScroll();
    };
  }, [modalId]);
}
